/*
|--------------------------------------------------------------------------
| Component: SearchBox.jsx
| Author: Sayed Ahmed
| Description: Search input field with debounced `onSearch` trigger.
| Features include a customizable placeholder, delay, and clear button.
|--------------------------------------------------------------------------
*/

import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

export default function SearchBox({
  searchValue, // Current search input value
  setSearchValue, // Function to update the search input
  onSearch, // Callback function triggered after debounce
  placeholder, // Optional placeholder text
  delay = 2000, // Debounce delay in milliseconds
}) {
  const { t } = useTranslation();
  const debounceRef = useRef(); // Ref to store timeout ID

  const firstRun = useRef(true);
  // Debounce effect for triggering search
  useEffect(() => {
    if (firstRun.current) {
      firstRun.current = false;
      return; // skip first run
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(() => {
      onSearch(searchValue?.trim() || "");
    }, delay);

    return () => clearTimeout(debounceRef.current);
  }, [searchValue]);

  // Clear the input and trigger empty search
  const handleClear = () => {
    setSearchValue("");
    onSearch("");
  };

  return (
    <div className="input-group search-area w-auto mr-2 relative">
      {/* Search Icon */}
      <span className="input-group-text">
        <i className="fa fa-search text-primary"></i>
      </span>

      {/* Search Input */}
      <input
        type="text"
        className="form-control pr-10"
        placeholder={placeholder || t("Search here...")}
        value={searchValue}
        onChange={(e) => setSearchValue(e.target.value)}
      />

      {/* Clear Button */}
      {searchValue && (
        <span
          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 cursor-pointer"
          onClick={handleClear}
          title={t("Clear")}
        >
          <i className="fa fa-times-circle" />
        </span>
      )}
    </div>
  );
}
